import { useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  EditableCategory,
  EditableCurrency,
  EditableSelect,
  EditableText,
} from '@/components/inventory/EditableItemField';
import { EditableDate } from '@/components/equipment/EditableDate';
import { EditableKV, KV } from '@/components/equipment/DetailRows';
import { WarrantyBadge } from '@/components/equipment/EquipmentBadges';
import { useVendors } from '@/features/procurement/hooks';
import {
  equipmentSchedule,
  useEquipmentFiles,
  useUpdateEquipment,
  type Equipment,
  type EquipmentPatch,
} from '@/features/equipment/hooks';
import { bookValueAtDisposal } from '@/features/equipment/depreciation';
import {
  DISPOSAL_METHODS,
  DISPOSAL_METHOD_LABELS,
  type DisposalMethod,
} from '@/features/equipment/model';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const NONE = '__none';

export function EquipmentPurchaseTab({
  equipment: e,
  canEdit,
  onOpenTab,
}: {
  equipment: Equipment;
  canEdit: boolean;
  onOpenTab: (tab: string) => void;
}) {
  const update = useUpdateEquipment();
  const { data: vendors } = useVendors();
  const { data: files } = useEquipmentFiles(e.id);

  const save = async (patch: EquipmentPatch) => {
    await update.mutateAsync({ id: e.id, patch });
  };

  const vendorOptions = useMemo(
    () => [...new Set((vendors ?? []).map((v) => v.name).filter(Boolean))].sort(),
    [vendors],
  );

  const paperwork = useMemo(() => {
    const counts = { invoice: 0, warranty: 0 };
    for (const f of files ?? []) {
      if (f.kind === 'invoice') counts.invoice += 1;
      if (f.kind === 'warranty') counts.warranty += 1;
    }
    return counts;
  }, [files]);

  const disposalBook = useMemo(() => bookValueAtDisposal(equipmentSchedule(e)), [e]);
  const gainLoss =
    e.disposed_on && disposalBook != null ? (e.disposal_proceeds_cents ?? 0) - disposalBook : null;

  const disposalOptions = useMemo(
    () => [
      { value: NONE, label: 'Not set' },
      ...DISPOSAL_METHODS.map((m) => ({ value: m, label: DISPOSAL_METHOD_LABELS[m] })),
    ],
    [],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Purchase</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <EditableKV
            label="Purchase date"
            editor={
              <EditableDate
                value={e.acquired_at}
                ariaLabel="Purchase date"
                canEdit={canEdit}
                onSave={(next) => save({ acquired_at: next })}
              />
            }
          />
          <EditableKV
            label="Placed in service"
            editor={
              <EditableDate
                value={e.in_service_on}
                ariaLabel="In-service date"
                placeholder="Same as purchase"
                canEdit={canEdit}
                onSave={(next) => save({ in_service_on: next })}
              />
            }
          />
          <EditableKV
            label="Purchase price"
            editor={
              <EditableCurrency
                value={e.purchase_price_cents}
                ariaLabel="Purchase price"
                canEdit={canEdit}
                density="kv"
                align="right"
                onSave={(next) => save({ purchase_price_cents: next })}
              />
            }
          />
          <EditableKV
            label="Purchased from"
            editor={
              <EditableCategory
                value={e.purchased_from}
                options={vendorOptions}
                ariaLabel="Purchased from"
                canEdit={canEdit}
                density="kv"
                align="right"
                placeholder="Vendor or seller"
                display={e.purchased_from ? <span>{e.purchased_from}</span> : undefined}
                onSave={(next) => save({ purchased_from: next })}
              />
            }
          />
          <EditableKV
            label="PO number"
            editor={
              <EditableText
                value={e.purchase_order_number ?? ''}
                variant="mono"
                maxLength={64}
                ariaLabel="PO number"
                canEdit={canEdit}
                density="kv"
                align="right"
                placeholder="—"
                onSave={(next) => save({ purchase_order_number: next })}
              />
            }
          />
          <EditableKV
            label="Invoice number"
            editor={
              <EditableText
                value={e.invoice_number ?? ''}
                variant="mono"
                maxLength={64}
                ariaLabel="Invoice number"
                canEdit={canEdit}
                density="kv"
                align="right"
                placeholder="—"
                onSave={(next) => save({ invoice_number: next })}
              />
            }
          />
          <KV
            label="Invoices & receipts"
            value={
              <button
                type="button"
                onClick={() => onOpenTab('documents')}
                className="min-h-9 text-atmosphere-dark hover:underline"
              >
                {paperwork.invoice === 0
                  ? 'Upload on Documents'
                  : `${paperwork.invoice} file${paperwork.invoice === 1 ? '' : 's'}`}
              </button>
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <CardTitle>Warranty</CardTitle>
            <WarrantyBadge expiresOn={e.warranty_expires_on} />
          </div>
        </CardHeader>
        <CardContent className="space-y-1">
          <EditableKV
            label="Provider"
            editor={
              <EditableText
                value={e.warranty_provider ?? ''}
                maxLength={120}
                ariaLabel="Warranty provider"
                canEdit={canEdit}
                density="kv"
                align="right"
                placeholder="Manufacturer, reseller or extended plan"
                onSave={(next) => save({ warranty_provider: next })}
              />
            }
          />
          <EditableKV
            label="Expires"
            editor={
              <EditableDate
                value={e.warranty_expires_on}
                ariaLabel="Warranty expiry"
                canEdit={canEdit}
                onSave={(next) => save({ warranty_expires_on: next })}
              />
            }
          />
          <div className="space-y-1 pt-2 text-sm">
            <span className="text-muted-foreground">Coverage & claim details</span>
            <EditableText
              value={e.warranty_terms ?? ''}
              multiline
              rows={4}
              maxLength={4000}
              ariaLabel="Warranty terms"
              canEdit={canEdit}
              density="kv"
              placeholder="What's covered, claim phone / email, contract number."
              onSave={(next) => save({ warranty_terms: next })}
            />
          </div>
          <KV
            label="Warranty documents"
            value={
              <button
                type="button"
                onClick={() => onOpenTab('documents')}
                className="min-h-9 text-atmosphere-dark hover:underline"
              >
                {paperwork.warranty === 0
                  ? 'Upload on Documents'
                  : `${paperwork.warranty} file${paperwork.warranty === 1 ? '' : 's'}`}
              </button>
            }
          />
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Disposal</CardTitle>
          <CardDescription>
            Setting a disposal date stops depreciation from that month and marks the equipment
            retired.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <EditableKV
            label="Disposed on"
            editor={
              <EditableDate
                value={e.disposed_on}
                ariaLabel="Disposal date"
                placeholder="Still owned"
                canEdit={canEdit}
                onSave={async (next) => {
                  const patch: EquipmentPatch = { disposed_on: next };
                  if (next && e.status !== 'retired') patch.status = 'retired';
                  await save(patch);
                  if (patch.status) toast.success('Marked as retired');
                }}
              />
            }
          />
          <EditableKV
            label="Method"
            editor={
              <EditableSelect
                value={e.disposal_method ?? NONE}
                options={disposalOptions}
                ariaLabel="Disposal method"
                canEdit={canEdit}
                density="kv"
                align="right"
                display={
                  e.disposal_method ? (
                    <span>
                      {DISPOSAL_METHOD_LABELS[e.disposal_method as DisposalMethod] ??
                        e.disposal_method}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )
                }
                onSave={(next) => save({ disposal_method: next === NONE ? null : next })}
              />
            }
          />
          <EditableKV
            label="Sale proceeds"
            editor={
              <EditableCurrency
                value={e.disposal_proceeds_cents}
                ariaLabel="Sale proceeds"
                canEdit={canEdit}
                density="kv"
                align="right"
                onSave={(next) => save({ disposal_proceeds_cents: next })}
              />
            }
          />
          <KV
            label="Gain / loss on disposal"
            value={
              gainLoss == null ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <span
                  className={cn(
                    'tabular-nums font-medium',
                    gainLoss >= 0 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark',
                  )}
                  title={`Proceeds minus book value of ${formatCurrency(disposalBook)}`}
                >
                  {gainLoss >= 0 ? '+' : '−'}
                  {formatCurrency(Math.abs(gainLoss))}
                </span>
              )
            }
          />
          <div className="space-y-1 pt-2 text-sm sm:col-span-2">
            <span className="text-muted-foreground">Disposal notes</span>
            <EditableText
              value={e.disposal_notes ?? ''}
              multiline
              rows={3}
              maxLength={4000}
              ariaLabel="Disposal notes"
              canEdit={canEdit}
              density="kv"
              placeholder="Buyer, reason, data-wipe confirmation, etc."
              onSave={(next) => save({ disposal_notes: next })}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
